import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Plus, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { authClient } from '@/lib/auth-client';
import { queryKeys } from '@/lib/query-keys';
import { useCurrentUser } from '@/hooks/use-current-user';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Passkey {
  id: string;
  name: string | null;
  credentialID: string;
  deviceType: string;
  createdAt: string | Date | null;
}

export default function ProfilePage(): React.ReactElement {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const [passkeyName, setPasskeyName] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Passkey | null>(null);

  const {
    data: passkeys,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.passkeys.list(),
    queryFn: async () => {
      const result = await authClient.passkey.listUserPasskeys();
      return (result.data ?? []) as Passkey[];
    },
  });

  const registerPasskey = useMutation({
    mutationFn: async (name: string) => {
      setIsRegistering(true);
      try {
        const result = await authClient.passkey.addPasskey({
          name: name || undefined,
        });
        if (result?.error) {
          throw new Error(result.error.message || 'Failed to register passkey');
        }
        return result;
      } finally {
        setIsRegistering(false);
      }
    },
    onSuccess: () => {
      toast.success('Passkey registered successfully');
      setPasskeyName('');
      queryClient.invalidateQueries({ queryKey: queryKeys.passkeys.all });
    },
  });

  const deletePasskey = useMutation({
    mutationFn: async (id: string) => {
      const result = await authClient.passkey.deletePasskey({ id });
      if (result?.error) {
        throw new Error(result.error.message || 'Failed to delete passkey');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Passkey deleted');
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.passkeys.all });
    },
  });

  const handleRegister = async (): Promise<void> => {
    await registerPasskey.mutateAsync(passkeyName);
  };

  const formatDate = (date: string | Date | null): string => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const initials = user
    ? `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`.toUpperCase()
    : '?';

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Profile</h2>
        <p className="text-sm text-muted-foreground">
          Your personal account information and security settings
        </p>
      </div>

      <div className="space-y-6">
        {/* Identity Section */}
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <Avatar className="h-14 w-14">
              {user && <AvatarImage src={`/api/users/${user.id}/avatar`} />}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </CardContent>
        </Card>

        {/* Passkeys Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Passkeys
            </CardTitle>
            <CardDescription>
              Passkeys add a second layer of security to your account. After signing in with your
              password, you&apos;ll verify your identity using biometrics (fingerprint, face
              recognition) or a security key.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Register New Passkey */}
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor="passkey-name">Passkey Name</Label>
                <Input
                  id="passkey-name"
                  value={passkeyName}
                  onChange={(e) => setPasskeyName(e.target.value)}
                  placeholder="e.g., MacBook Pro, iPhone"
                  disabled={isRegistering}
                />
              </div>
              <Button onClick={handleRegister} disabled={isRegistering}>
                <Plus className="mr-2 h-4 w-4" />
                {isRegistering ? 'Registering...' : 'Add Passkey'}
              </Button>
            </div>

            {/* Passkey List */}
            {isLoading && <p className="text-sm text-muted-foreground">Loading passkeys...</p>}

            {error && (
              <p className="text-sm text-destructive">Failed to load passkeys. Please try again.</p>
            )}

            {passkeys && passkeys.length === 0 && (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <Fingerprint className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No passkeys registered. Add a passkey to enable two-factor authentication.
                </p>
              </div>
            )}

            {passkeys && passkeys.length > 0 && (
              <div className="space-y-3">
                {passkeys.map((pk) => (
                  <div
                    key={pk.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="flex items-center gap-3">
                      <Fingerprint className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{pk.name || 'Unnamed Passkey'}</p>
                        <p className="text-xs text-muted-foreground">
                          {pk.deviceType === 'singleDevice' ? 'Single device' : 'Multi-device'} ·
                          Added {formatDate(pk.createdAt)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteTarget(pk)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Passkey</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the passkey &quot;
              {deleteTarget?.name || 'Unnamed Passkey'}
              &quot;? You will no longer be able to use it to sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deletePasskey.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePasskey.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
